// react
import {
	ChangeEvent,
	Dispatch,
	FormEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { ErrorCause, Feedback, LoadingStatus, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// fields
import { Input } from '../../fields/Input/Input';

// mui
import { Alert, AlertTitle, Grid } from '@mui/material';

// utils
import { translateError } from '../../../utils/translations';

// props
export interface FormAddWithdrawalApplicationProps {
	idForm?: string;
	isOpen: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
	transaction?: Transaction;
}

export const FormAddWithdrawalApplication = ({
	idForm = 'form-add-withdrawal-application',
	isOpen,
	onClose,
	onSuccess,
	setStatus,
	transaction,
}: FormAddWithdrawalApplicationProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({ borrowerReference: '' });

	// vars
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	const handleChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setValues((valuesPrev) => ({
			...valuesPrev,
			[name]: value,
		}));
	};

	useEffect(() => {
		if (!isOpen) {
			setFeedback(null);
			setValues({ borrowerReference: '' });
		}
	}, [isOpen]);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setStatus('LOADING');
		setFeedback(null);

		try {
			const body = {
				borrowerReference: values.borrowerReference,
				isInitiating: true,
				project: project?.id,
				transaction: transaction?.id || Number(paramDialogTransaction),
			};

			const res = await fetch(`${process.env.REACT_APP_API_URL}/withdrawal-applications`, {
				method: 'POST',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const response = await res.json();

			if (response.error) {
				const errorId =
					response.error.id && response.error.id !== 'UNKNOWN'
						? response.error.id
						: 'WITHDRAWAL_APPLICATION_ERROR';
				throw new Error(response.error.message || t('error.default'), {
					cause: {
						...response.error,
						id: errorId,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			setBanner({
				hasClose: true,
				message: t('alert.withdrawalApplicationInitiated'),
				severity: 'success',
			});

			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			setFeedback({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});
		} finally {
			setStatus('DEFAULT');
		}
	};

	return (
		<form id={idForm} name="formAddWithdrawalApplication" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-borrowerReference`}
						label={t('borrowerReference#')}
						name="borrowerReference"
						onChange={handleChangeValue}
						required={true}
						value={values.borrowerReference}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
